import { AxiosInstance, AxiosResponse } from "axios";
import { Pagination } from "../types/PaginiationResult";
import { OrderSummary } from "../types/Order/orderSummary";
import { OrderListParams } from "../types/Order/OrderListParams";
import { Order } from "../types/Order/Order";
import { PaginationParams } from "../types/PaginationParams";
import { OrderItemSummary } from "../types/Order/orderItemSummary";
import { OrderLineItem } from "../types/Order/OrderLineItem";
import {
  WfsBadRequestResponse,
  WfsDataResponse,
  WfsDeferredRequestResponse,
  WfsPaymentRequiredResponse,
  WfsResponseType,
} from "../types/Wfs/WfsResponse";
import { Stats } from "../types/Order/Stats";

export default class OrderService {
  private _lbBaseUrl = process.env.REACT_APP_LB_URL;
  private _endpoint = "Order";
  private _client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this._client = client;
  }

  public async GetOrderItemData(
    orderId: string,
    orderItemId: string,
  ): Promise<string> {
    return this._client
      .get<any>(`${this._endpoint}/${orderId}/${orderItemId}?redirect=false`, {
        baseURL: this._lbBaseUrl,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async listOrders(
    { page, pageSize, filterExpression, sortExpression }: OrderListParams,
    abortController?: AbortController,
  ): Promise<Pagination<OrderSummary[]>> {
    const queryString = new URLSearchParams();
    queryString.append("page", page.toString());
    if (pageSize) {
      queryString.append("pageSize", pageSize.toString());
    }
    if (filterExpression) {
      queryString.append("filterExpression", filterExpression);
    }
    if (sortExpression) {
      queryString.append("sortExpression", sortExpression);
    }

    return this._client
      .get<any>(`${this._endpoint}?${queryString}`, {
        signal: abortController?.signal,
      })
      .then((response) => {
        return response.data;
      });
  }

  public async GetOrderById(
    orderId: string,
    abortController?: AbortController,
  ): Promise<Order> {
    return this._client
      .get<any>(`${this._endpoint}/${orderId}`, {
        signal: abortController?.signal,
      })
      .then((response) => {
        return response.data;
      });
  }

  public ListOrderItems(
    { page, pageSize, filterExpression, sortExpression }: PaginationParams,
    abortController?: AbortController,
  ): Promise<Pagination<OrderItemSummary>> {
    const queryString = new URLSearchParams();
    queryString.append("page", page.toString());
    if (pageSize) {
      queryString.append("pageSize", pageSize.toString());
    }
    if (filterExpression) {
      queryString.append("filterExpression", filterExpression);
    }
    if (sortExpression) {
      queryString.append("sortExpression", sortExpression);
    }

    return this._client
      .get<any>(`${this._endpoint}/ListOrderItems?${queryString}`, {
        signal: abortController?.signal,
      })
      .then((response) => {
        return response.data;
      });
  }

  public GetOrderItem(
    orderId: string,
    orderItemId: string,
  ): Promise<OrderLineItem | undefined> {
    return this.GetOrderById(orderId).then((order) => {
      return order.lineItems.find((lineItem) => lineItem.id === orderItemId);
    });
  }

  public async ReOrder(
    orderId: string,
    orderItemId: string,
    currencyCode?: string,
  ): Promise<
    | WfsDataResponse
    | WfsDeferredRequestResponse
    | WfsPaymentRequiredResponse
    | WfsBadRequestResponse
  > {
    try {
      var response: AxiosResponse<Blob> = await this._client.post(
        `${this._endpoint}/${orderId}/${orderItemId}/ReOrder${currencyCode ? `?currencyCode=${currencyCode}` : ""}`,
        {},
        {
          baseURL: this._lbBaseUrl,
          timeout: 0,
          //@ts-ignore - suppressAlert isn't defined on the Axios Config interface
          suppressAlert: true,
          responseType: "blob",
        },
      );
      switch (response.status) {
        case 200:
          return this.HandleWfsDataResponse(response);
        case 202:
          return await this.HandleWfsDeferredRequestResponse(response);
        default:
          throw new Error("Unknown response type");
      }
    } catch (error: any) {
      switch (error.response?.status) {
        case 400:
        case 500:
          return await this.HandleWfsBadRequestResponse(error.response);
        case 402:
          return await this.HandleWfsPaymentRequiredResponse(error.response);
        default:
          throw error;
      }
    }
  }

  public Stats(): Promise<Stats> {
    return this._client.get<any>(`${this._endpoint}/Stats`).then((response) => {
      return response.data;
    });
  }

  public AssignOrder(orderId: string, userId: string): Promise<void> {
    return this._client.post(`${this._endpoint}/${orderId}/Assign/${userId}`);
  }

  public GetOrderItemById(
    orderItemId: string,
    abortController?: AbortController,
  ): Promise<OrderLineItem> {
    return this._client
      .get<any>(`${this._endpoint}/OrderItem/${orderItemId}`, {
        signal: abortController?.signal,
      })
      .then((response) => response.data);
  }

  public SetOrderItemDataDeleted(orderItemId: string): Promise<void> {
    return this._client
      .post(`${this._endpoint}/OrderItem/${orderItemId}/SetDataDeleted`)
      .then(() => undefined);
  }

  private HandleWfsDataResponse(
    response: AxiosResponse<Blob>,
  ): WfsDataResponse {
    const fileName = this.GetFilename(response);
    return {
      type: WfsResponseType.Data,
      data: new Blob([response.data]),
      fileName: fileName,
    };
  }

  private async HandleWfsDeferredRequestResponse(
    response: AxiosResponse<any>,
  ): Promise<WfsDeferredRequestResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.DeferredRequest,
      deferredRequestId: data.requestId,
    };
  }

  private async HandleWfsBadRequestResponse(
    response: AxiosResponse<Blob>,
  ): Promise<WfsBadRequestResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.BadRequest,
      error:
        typeof data === "string" ? data : (
          "Something went wrong with your request"
        ),
    };
  }

  private async HandleWfsPaymentRequiredResponse(
    response: AxiosResponse<any, any>,
  ): Promise<WfsPaymentRequiredResponse> {
    const data = await this.ReadDataFromBlob(response.data);
    return {
      type: WfsResponseType.PaymentRequired,
      id: data.id,
      description: data.description,
      layerName: data.layerName,
      priceExTax: data.priceExTax,
      priceIncTax: data.priceIncTax,
      transactionCurrencyCode: data.transactionCurrencyCode,
      taxCode: data.taxCode,
      isPremium: data.isPremium,
      parameters: data.parameters,
    };
  }

  private GetFilename(response: AxiosResponse) {
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition !== null) {
      const pattern = new RegExp("filename=([^;$]*)");
      const match = pattern.exec(response.headers["content-disposition"]);
      if (match && match.length >= 2) return match[1];
    }

    throw new Error("Could not get filename");
  }

  private async ReadDataFromBlob(data: Blob): Promise<any> {
    const blobText = await data.text();
    try {
      const object = JSON.parse(blobText);
      return object;
    } catch {
      return blobText;
    }
  }
}
